import { useState, useEffect } from "react";
import { useHLAuth } from "@/context/HLAuthContext";
import { useNavigate } from "react-router-dom";
import { Save, LogOut, Video, Loader2, CheckCircle, User } from "lucide-react";

const AVAILABILITY_OPTIONS = ["Immediately", "Within 1 month", "Within 3 months", "Not actively looking"];

export default function HLAspirantDashboard() {
  const { user, logout, api } = useHLAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/aspirant/profile").then(r => setProfile(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const update = (k, v) => setProfile({ ...profile, [k]: v });
  const updateSkills = (val) => update("skills", val.split(",").map(s => s.trim()).filter(Boolean));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/aspirant/profile", profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  const handleLogout = () => { logout(); navigate("/humanloop/aspirant"); };

  if (loading || !profile) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>;

  const inputClass = "w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-sm text-slate-200 placeholder-slate-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all";
  const labelClass = "text-xs font-medium text-slate-400 mb-1.5 block";
  const completeness = [profile.name, profile.current_role, profile.video_url, profile.q_aspiration, profile.skills?.length > 0].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="hl-aspirant-dashboard">
      <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-400" />
            <span className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>My Profile</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{user?.email}</span>
            <button onClick={handleLogout} className="text-xs text-slate-400 hover:text-red-400 flex items-center gap-1 transition-colors" data-testid="hl-logout"><LogOut size={12} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Completeness */}
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">Profile completeness</span>
            <span className="text-xs font-bold text-emerald-400">{completeness}/5</span>
          </div>
          <div className="h-1.5 bg-slate-800 rounded-full"><div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${(completeness / 5) * 100}%` }} /></div>
        </div>

        {/* Basic info */}
        <section className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6">
          <h2 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={labelClass}>Full Name *</label><input className={inputClass} value={profile.name || ""} onChange={e => update("name", e.target.value)} data-testid="hl-field-name" /></div>
            <div><label className={labelClass}>Phone</label><input className={inputClass} value={profile.phone || ""} onChange={e => update("phone", e.target.value)} placeholder="+91..." /></div>
            <div><label className={labelClass}>Location</label><input className={inputClass} value={profile.location || ""} onChange={e => update("location", e.target.value)} placeholder="City, State" /></div>
            <div><label className={labelClass}>Headline</label><input className={inputClass} value={profile.headline || ""} onChange={e => update("headline", e.target.value)} placeholder="e.g. Product Manager | 5 yrs | Ex-Flipkart" /></div>
          </div>
        </section>

        {/* Professional info */}
        <section className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6">
          <h2 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Professional Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={labelClass}>Current Role *</label><input className={inputClass} value={profile.current_role || ""} onChange={e => update("current_role", e.target.value)} placeholder="e.g. Senior Software Engineer" /></div>
            <div><label className={labelClass}>Desired Role</label><input className={inputClass} value={profile.desired_role || ""} onChange={e => update("desired_role", e.target.value)} placeholder="e.g. Engineering Manager" /></div>
            <div><label className={labelClass}>Experience (years)</label><input type="number" min="0" max="15" step="0.5" className={inputClass} value={profile.experience_years || 0} onChange={e => update("experience_years", parseFloat(e.target.value) || 0)} /></div>
            <div><label className={labelClass}>Education</label><input className={inputClass} value={profile.education || ""} onChange={e => update("education", e.target.value)} placeholder="e.g. B.Tech from IIT Delhi, MBA from IIM" /></div>
            <div><label className={labelClass}>Skills * (comma-separated)</label><input className={inputClass} value={(profile.skills || []).join(", ")} onChange={e => updateSkills(e.target.value)} placeholder="Python, Product Management, Data Analysis" /></div>
            <div><label className={labelClass}>Salary Expectation</label><input className={inputClass} value={profile.salary_expectation || ""} onChange={e => update("salary_expectation", e.target.value)} placeholder="e.g. INR 15-20 LPA" /></div>
            <div>
              <label className={labelClass}>Availability</label>
              <select className={inputClass} value={profile.availability || ""} onChange={e => update("availability", e.target.value)}>
                <option value="">Select...</option>
                {AVAILABILITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
        </section>

        {/* Video pitch */}
        <section className="bg-slate-900/40 border border-emerald-500/20 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Video className="w-5 h-5 text-emerald-400" />
            <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>2-Minute Video Pitch *</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Record a 2-minute video introducing yourself — who you are, what you've done, and what you're looking for. Upload to YouTube (unlisted) or Loom and paste the link below.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2"><label className={labelClass}>Video URL</label><input className={inputClass} value={profile.video_url || ""} onChange={e => update("video_url", e.target.value)} placeholder="https://youtube.com/watch?v=... or https://loom.com/share/..." data-testid="hl-field-video" /></div>
            <div>
              <label className={labelClass}>Video Platform</label>
              <select className={inputClass} value={profile.video_type || ""} onChange={e => update("video_type", e.target.value)}>
                <option value="">Select...</option>
                <option value="youtube">YouTube</option>
                <option value="loom">Loom</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          {profile.video_url && (
            <div className="mt-4 aspect-video rounded-xl overflow-hidden bg-slate-800 max-w-lg">
              <iframe src={profile.video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/").replace("loom.com/share/", "loom.com/embed/")} title="Pitch video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
            </div>
          )}
        </section>

        {/* 3 Questions */}
        <section className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6">
          <h2 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>3 Questions That Define You</h2>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>1. What is your end aspiration / goal? *</label>
              <textarea className={`${inputClass} resize-none`} rows={3} value={profile.q_aspiration || ""} onChange={e => update("q_aspiration", e.target.value)} placeholder="Where do you see yourself in 5-10 years? What impact do you want to make?" data-testid="hl-field-q1" />
            </div>
            <div>
              <label className={labelClass}>2. What skills do you currently possess? (can be outside your corporate role)</label>
              <textarea className={`${inputClass} resize-none`} rows={3} value={profile.q_skills_outside || ""} onChange={e => update("q_skills_outside", e.target.value)} placeholder="Include skills beyond your job — teaching, content creation, sports, community work, etc." />
            </div>
            <div>
              <label className={labelClass}>3. Why should a company hire you? (2-3 sentences)</label>
              <textarea className={`${inputClass} resize-none`} rows={3} value={profile.q_why_hire || ""} onChange={e => update("q_why_hire", e.target.value)} placeholder="What's your unique value? What do you bring that others don't?" />
            </div>
          </div>
        </section>

        {/* Save */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-600">Fields marked * are required for your profile to be visible to corporates</p>
          <button onClick={handleSave} disabled={saving} className="bg-emerald-500 text-slate-950 px-8 py-3 rounded-full font-bold hover:bg-emerald-400 transition-all flex items-center gap-2 disabled:opacity-50" data-testid="hl-save-profile">
            {saving ? <Loader2 size={16} className="animate-spin" /> : saved ? <><CheckCircle size={16} /> Saved!</> : <><Save size={16} /> Save Profile</>}
          </button>
        </div>
      </main>
    </div>
  );
}
